import { useLatest } from 'ahooks';
import { useSessionContext } from '@/contexts/session/context';
import cryptoManager from '@/scripts/crypto';
import { chatApis } from '@/configs/apis/chat';
import { pushLog } from '@/scripts/utils/devUtils';
import { UserKeyPackageType } from '@/configs/apis/type';
import { withRetry } from '@/scripts/utils';
import type { ChatMessageMessageIdParams } from '../useChatMessages/types';
import { ChatMessageCategory } from '../useChatMessages/types';

const defaultParams: any = {
  version: import.meta.env.VITE_ENCRYPT_SINGLE_VERSION,
  type: UserKeyPackageType.SINGLE,
};
export function useSingleEncryption() {
  const {
    session: { user },
  } = useSessionContext();
  const [hasSingleKey, setHasSingleKey] = useState(true);
  const getTargetChatPubKeys = useCallback(async (receiverId: string) => {
    try {
      const baseParams = { ...defaultParams, userId: receiverId };
      let userPackage = await chatApis.getUserPackage(baseParams);
      if (userPackage) {
        cryptoManager.setKeyPackage(receiverId, userPackage);
      } else {
        const keyPackageInfo = await chatApis.getTargetKeyPackage(receiverId);
        if (keyPackageInfo?.key_package) {
          userPackage = keyPackageInfo.key_package;
          cryptoManager.setKeyPackage(receiverId, keyPackageInfo.key_package);
          chatApis.setUserPackage({
            ...baseParams,
            keyPackage: keyPackageInfo.key_package,
          });
        }
      }
      return userPackage;
    } catch (error: any) {
      pushLog('single-getTargetKey', error?.message);
      return null;
    }
  }, []);

  const checkAndSetKeyPackage = useCallback(async (userId: string) => {
    try {
      const baseParams = { ...defaultParams, userId };
      let userKeyPackage = await chatApis.getUserPackage(baseParams);
      if (!userKeyPackage) {
        userKeyPackage = cryptoManager.getKeyPacakge();
        await chatApis.sendKeyPackage({
          key_package: [userKeyPackage],
        });
        chatApis.setUserPackage({ ...baseParams, userKeyPackage });
      }
      setHasSingleKey(true);
      return userKeyPackage;
    } catch (error: any) {
      pushLog('single-keyPackage-check', error?.message);
      setHasSingleKey(false);
      throw error;
    }
  }, []);

  const encryptMessage = useCallback(
    async (receiverId: string, text: string) =>
      await withRetry(async () => cryptoManager.encryptMessage(receiverId, text), {
        maxRetries: 1,
        shouldRetry: (error) => error.message === '-10',
        beforeRetry: () => getTargetChatPubKeys(receiverId),
        onError: (error) => {
          pushLog('single-encryption', error?.message);
          return error?.message;
        },
      })(),
    [getTargetChatPubKeys]
  );

  const decryptMessage = useCallback(
    async (message: ChatMessageMessageIdParams) =>
      await withRetry(
        async () =>
          cryptoManager.decryptMessage(
            message.category === ChatMessageCategory.SEND
              ? message.receiverId!
              : message.senderId!,
            message.payload!,
            message.category === ChatMessageCategory.SEND
          ),
        {
          maxRetries: 1,
          shouldRetry: (error) => error.message === '-10',
          beforeRetry: () => getTargetChatPubKeys(message.senderId!),
          onError: (error) => {
            pushLog('single-decryption', error?.message);
            return `Decryption failed (${error?.message})`;
          },
        }
      )(),
    [getTargetChatPubKeys]
  );

  const initCrypto = useCallback(
    async (userId: string) => {
      try {
        await cryptoManager.initCrypto(userId);
        await withRetry(() => checkAndSetKeyPackage(userId), {
          beforeRetry: () => new Promise((resolve) => setTimeout(resolve, 1000)),
        })();
      } catch (error: any) {
        pushLog('single-initCrypto', error?.message);
      }
    },
    [checkAndSetKeyPackage]
  );

  const latest = useLatest({ initCrypto });

  useLayoutEffect(() => {
    if (user?.userId) {
      latest.current?.initCrypto(user?.userId);
    }
  }, [user, latest]);

  const value = useMemo(
    () => ({
      getTargetChatPubKeys,
      checkAndSetKeyPackage,
      encryptMessage,
      decryptMessage,
      hasSingleKey,
    }),
    [
      getTargetChatPubKeys,
      checkAndSetKeyPackage,
      encryptMessage,
      decryptMessage,
      hasSingleKey,
    ]
  );

  return value;
}
